/*
TODO: Implement commented out code (PayNow/Onboarding/Tooltip) for mobile for
SummaryCard to be a truly shared component
*/

import React, { useRef } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import MonthlyUsageBars from './MonthlyUsageBars';

import {
  useConsumerUsageSummaryQuery,
  useConsumerStatusSummaryQuery,
} from 'apollo/queries/autogenerate/hooks';
import { AppleCreditStatus } from 'apollo/queries/autogenerate/schemas';
import Icon from 'shared/modules/common/components/atoms/Icon';
import colors from 'shared/modules/common/constants/colors';
import AmountRow from 'shared/modules/usage/components/organisms/SummaryCard/AmountRow';
import { MONTHLY_USAGE_BAR_MIN_MONTHS } from 'shared/modules/usage/utils/utils';
import eventBus from 'shared/utils/event-bus';
import { formatToCurrency } from 'shared/utils/japanese';

type Props = {
  isPlusWithApple: boolean;
};

export default function SummaryCard({ isPlusWithApple }: Props) {
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  const { data } = useConsumerUsageSummaryQuery();
  const { data: consumerStatusSummary } = useConsumerStatusSummaryQuery();
  const monthlyUsageBarScrollviewRef = useRef<any>();
  const paidyPlusOutstandingDebts =
    data?.consumerUsageSummary.outstandingDebts.paidyPlus;
  const appleOutstandingDebts =
    data?.consumerUsageSummary.outstandingDebts.apple;

  const isAppleStatusPending = (status: AppleCreditStatus | undefined) => {
    if (
      status &&
      [
        AppleCreditStatus.AWAITING_CIC_RESULTS,
        AppleCreditStatus.AWAITING_PAC_ASSESSMENT,
        AppleCreditStatus.PAC_DATA_REQUIRED,
      ].includes(status)
    ) {
      return true;
    }

    return false;
  };

  const getAppleUsageValue = () => {
    if (isPlusWithApple) {
      return formatToCurrency(appleOutstandingDebts ?? 0);
    } else if (
      isAppleStatusPending(consumerStatusSummary?.appleCreditStatus.status)
    ) {
      return '手続き中'; // Apple Pending Message
    } else {
      return '今すぐ申し込む'; // Apply
    }
  };

  const handleOnPressTRE = () => {
    // Scroll to end
    monthlyUsageBarScrollviewRef.current?.scrollToEnd({
      animated: true,
    });
  };

  const handleOnPressAppleUsage = () => {
    eventBus.emit('appdl:open');
  };

  return (
    <View
      style={[
        styles.container,
        isBigScreen && {
          flexDirection: 'row',
        },
      ]}
    >
      <View
        style={[
          styles.amountContainer,
          isBigScreen && styles.amountContainerBigScreen,
        ]}
      >
        <View
          style={[
            styles.card,
            styles.mainSection,
            isBigScreen && styles.mainSectionBigScreen,
            styles.amountRow,
            {
              marginRight: isBigScreen ? 0 : 8,
              marginBottom: isBigScreen ? 8 : 0,
              minWidth: isBigScreen ? 184 : 'auto',
            },
          ]}
        >
          <AmountRow
            icon={
              <Icon name="shopping-cart" size="24" style={{ marginRight: 8 }} />
            }
            text={`ご利用総額`}
            value={formatToCurrency(paidyPlusOutstandingDebts ?? 0)}
            amountTestID="summary-card-usage-amount"
            disabled={
              (data?.consumerUsageSummary?.monthlyTotalUsagesV3.length || 0) <=
              MONTHLY_USAGE_BAR_MIN_MONTHS
            }
            onPress={handleOnPressTRE}
          />
        </View>
        <View
          style={[
            styles.card,
            styles.mainSection,
            isBigScreen && styles.mainSectionBigScreen,
            styles.amountRow,
            {
              marginLeft: isBigScreen ? 0 : 8,
              marginTop: isBigScreen ? 8 : 0,
              minWidth: isBigScreen ? 184 : 'auto',
            },
          ]}
        >
          <AmountRow
            icon={
              <Icon
                name="apple-special-offers"
                size="24"
                style={{ marginRight: 8 }}
              />
            }
            text={`Apple利用総額`}
            value={getAppleUsageValue()}
            amountTestID="apple-summary-card-usage-amount"
            disabled={
              isAppleStatusPending(
                consumerStatusSummary?.appleCreditStatus.status
              )
                ? true
                : false
            }
            onPress={handleOnPressAppleUsage}
          />
        </View>
      </View>
      <View
        style={[
          styles.card,
          styles.mainSection,
          isBigScreen && styles.mainSectionBigScreen,
          {
            display: 'flex',
            flex: 1,
          },
        ]}
      >
        <View style={styles.monthlyUsageBarsContainer}>
          {(data?.consumerUsageSummary?.monthlyTotalUsagesV3.length || 0) >
            MONTHLY_USAGE_BAR_MIN_MONTHS && (
            <View style={styles.caretRightIcon}>
              <Icon name="caret-right-light" size="24" />
            </View>
          )}
          <MonthlyUsageBars
            monthlyUsageBarScrollviewRef={monthlyUsageBarScrollviewRef}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {},
  card: {
    borderRadius: 24,
    backgroundColor: colors.white,
    ...Platform.select({
      android: {
        elevation: 2,
      },
      ios: {
        shadowColor: 'rgba(0, 0, 0, 0.08)',
        shadowOpacity: 1,
        shadowRadius: 8,
        shadowOffset: {
          width: 0,
          height: 2,
        },
      },
    }),
  },
  mainSection: {
    padding: 24,
  },
  mainSectionBigScreen: {
    flexDirection: 'row',
  },
  amountContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 16,
    marginTop: 16,
  },
  amountContainerBigScreen: {
    flexDirection: 'column',
    marginBottom: 0,
    marginTop: 0,
    marginRight: 16,
  },
  amountRow: {
    flex: 1,
    borderRadius: 12,
    padding: 16,
  },
  caretRightIcon: {
    position: 'absolute',
    top: '40%',
    right: -10,
  },
  monthlyUsageBarsContainer: {
    width: '100%',
  },
});
